import React, { memo, useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import { quickFillActionTooltips } from '../../config/actionHelpCatalog';
import { borderRadius, getControlBorderColor, layoutGap } from '../../../theme/theme';
import {
  getWorkflowWorkspacePalette,
  getWorkspaceIconButtonSx,
} from '../workflow/WorkflowWorkspacePrimitives';
import {
  getManagedColumnPresentation,
  getParameterIdentityKey,
  slugifyParameterName,
} from './propertySelectionModel';

export interface PropertyItem {
  id: string;
  label: string;
  checked: boolean;
  revitParameter?: string;
  isReadOnly?: boolean;
  dataType?: string;
  isInstance?: boolean;
}

export interface ParameterOption {
  value: string;
  label: string;
}

export interface AvailableParameter {
  name: string;
  dataType: string;
  category: string;
  isReadOnly: boolean;
  isInstance?: boolean;
}

export interface PropertySelectionPanelProps {
  properties: PropertyItem[];
  extraProperties?: PropertyItem[];
  sharedParameters: ParameterOption[];
  availableParameters?: AvailableParameter[];
  workflowMode: 'standard' | 'multipage' | 'legacy';
  workflowParameters: Array<{ name: string }>;
  onPropertyCheckedChange: (propertyId: string, checked: boolean) => void;
  onParameterChange: (propertyId: string, parameter: string) => void;
  onAddExtraProperty?: (property: PropertyItem) => void;
  onRemoveExtraProperty?: (propertyId: string) => void;
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  highlightedLabels?: string[];
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

/**
 * Dockable panel for adding extra columns to QuickFill table
 * Shows available family parameters as a list
 */
export const PropertySelectionPanel = memo<PropertySelectionPanelProps>(({
  properties,
  extraProperties = [],
  sharedParameters,
  availableParameters = [],
  workflowMode,
  workflowParameters,
  onPropertyCheckedChange,
  onParameterChange,
  onAddExtraProperty,
  onRemoveExtraProperty,
  expanded = true,
  onExpandChange,
  highlightedLabels = [],
  searchInputRef,
}) => {
  const theme = useTheme();
  const isShortViewport = useMediaQuery('(max-height:480px)');
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const panelTitleId = 'quickfill-column-panel-title';
  const panelDescriptionId = 'quickfill-column-panel-description';

  const [searchTerm, setSearchTerm] = useState('');
  const [filterReadOnly, setFilterReadOnly] = useState(false); // Show all parameters by default including read-only
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const normalizedHighlights = useMemo(
    () => highlightedLabels.map((value) => value.trim().toUpperCase()).filter(Boolean),
    [highlightedLabels]
  );

  const managedItems = useMemo(() => {
    const existing = [
      ...properties.map((property) => ({
        id: property.id,
        label: property.label,
        revitParameter: property.revitParameter || '',
        checked: property.checked,
        isReadOnly: property.isReadOnly || false,
        dataType: property.dataType || '',
        isInstance: property.isInstance,
        source: 'existing' as const,
      })),
      ...extraProperties.map((property) => ({
        id: property.id,
        label: property.label,
        revitParameter: property.revitParameter || '',
        checked: property.checked,
        isReadOnly: property.isReadOnly || false,
        dataType: property.dataType || '',
        isInstance: property.isInstance,
        source: 'extra' as const,
      })),
    ];

    const existingParameterKeys = new Set(
      existing.map((item) => getParameterIdentityKey(item.revitParameter)).filter(Boolean)
    );
    const availableByName = new Map<string, AvailableParameter>();
    availableParameters.forEach((parameter) => {
      const key = getParameterIdentityKey(parameter.name);
      if (key && !availableByName.has(key)) {
        availableByName.set(key, parameter);
      }
    });

    const available = Array.from(availableByName.values())
      .filter((parameter) => !existingParameterKeys.has(getParameterIdentityKey(parameter.name)))
      .map((parameter) => ({
        id: `available-${slugifyParameterName(parameter.name)}`,
        label: parameter.name,
        revitParameter: parameter.name,
        checked: false,
        isReadOnly: parameter.isReadOnly,
        dataType: parameter.dataType,
        isInstance: parameter.isInstance,
        category: parameter.category || 'General',
        parameter,
        source: 'available' as const,
      }));

    const combined = [...existing, ...available];
    const query = searchTerm.trim().toLowerCase();

    return combined
      .filter((item) => {
        const matchesSearch =
          query === '' ||
          item.label.toLowerCase().includes(query) ||
          item.revitParameter.toLowerCase().includes(query) ||
          ('category' in item && item.category ? item.category.toLowerCase().includes(query) : false);
        const matchesReadOnly = !filterReadOnly || !item.isReadOnly;
        return matchesSearch && matchesReadOnly;
      })
      .sort((left, right) => {
        if (left.checked !== right.checked) {
          return left.checked ? -1 : 1;
        }
        if (left.source !== right.source) {
          if (left.source === 'existing') return -1;
          if (right.source === 'existing') return 1;
          if (left.source === 'extra') return -1;
          if (right.source === 'extra') return 1;
        }
        return left.label.localeCompare(right.label);
      });
  }, [availableParameters, extraProperties, filterReadOnly, properties, searchTerm]);
  const visibleCount = useMemo(
    () => managedItems.filter((item) => item.checked).length,
    [managedItems]
  );

  useEffect(() => {
    const firstMatch = managedItems.find((item) =>
      normalizedHighlights.includes(item.label.trim().toUpperCase())
    );

    if (!firstMatch) {
      return;
    }

    const node = itemRefs.current[firstMatch.id];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [managedItems, normalizedHighlights]);

  const handleAddParameter = (param: AvailableParameter) => {
    if (!onAddExtraProperty) return;

    onAddExtraProperty({
      id: `extra-${slugifyParameterName(param.name)}`,
      label: param.name,
      checked: true,
      revitParameter: param.name,
      isReadOnly: param.isReadOnly,
      dataType: param.dataType,
    });
  };

  const handleRemoveExtra = (id: string) => {
    if (onRemoveExtraProperty) {
      onRemoveExtraProperty(id);
    }
  };

  const handleItemClick = (item: (typeof managedItems)[number]) => {
    if (item.source === 'available') {
      handleAddParameter(item.parameter);
      return;
    }

    onPropertyCheckedChange(item.id, !item.checked);
  };

  return (
    <Paper
      elevation={0}
      role="complementary"
      aria-labelledby={panelTitleId}
      aria-describedby={panelDescriptionId}
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: `${borderRadius.none}px`,
        color: workspacePalette.bodyText,
        backgroundColor: workspacePalette.paneBackground,
        borderLeft: `1px solid ${workspacePalette.paneBorder}`,
      }}
      data-short-viewport={isShortViewport ? 'true' : undefined}
    >
      <Box
        sx={{
          px: { xs: 0.9, md: 1.1 },
          py: isShortViewport ? 0.45 : 0.8,
          borderBottom: `1px solid ${workspacePalette.paneDivider}`,
          backgroundColor: workspacePalette.sectionRaisedBackground,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense }}>
              <Typography id={panelTitleId} variant="subtitle2" sx={{ fontWeight: 700, color: workspacePalette.headingText }}>
                Manage columns
              </Typography>
              <HelpTooltipTrigger
                label="Manage columns"
                help={quickFillActionTooltips.manageColumns}
                placement="bottom-start"
              />
            </Box>
            <Typography
              id={panelDescriptionId}
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'block',
                mt: isShortViewport ? 0.1 : 0.25,
                lineHeight: 1.35,
              }}
            >
              {visibleCount} visible / {managedItems.length} total
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Close manage columns panel"
            onClick={() => onExpandChange?.(false)}
            sx={[getWorkspaceIconButtonSx(theme, { size: 28 }), { flexShrink: 0 }]}
          >
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 0.9, md: 1.1 }, py: isShortViewport ? 0.5 : 0.75, borderBottom: `1px solid ${workspacePalette.paneDivider}` }}>
        <ActionHelpTooltip help={quickFillActionTooltips.columnSearch} placement="bottom-start">
          <TextField
            inputRef={searchInputRef}
            fullWidth
            size="small"
            placeholder="Search columns"
            inputProps={{ 'aria-label': 'Search available parameters' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1.2rem', color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: isShortViewport ? 0.45 : 0.75,
              '& .MuiOutlinedInput-root': {
                minHeight: isShortViewport ? 30 : 32,
                borderRadius: `${borderRadius.sm}px`,
                backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.76 : 0.18),
                '& fieldset': {
                  borderColor: getControlBorderColor(theme),
                },
                '&:hover fieldset': {
                  borderColor: getControlBorderColor(theme),
                },
              },
            }}
          />
        </ActionHelpTooltip>
        <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" useFlexGap flexWrap="wrap">
          <ActionHelpTooltip help={quickFillActionTooltips.hideReadOnly} placement="bottom-start">
            <Chip
              size="small"
              clickable
              label={filterReadOnly ? 'Editable only' : 'All columns'}
              aria-pressed={filterReadOnly}
              onClick={() => setFilterReadOnly((prev) => !prev)}
              sx={{
                height: 18,
                borderRadius: `${borderRadius.full}px`,
                fontSize: '0.64rem',
                fontWeight: 600,
                backgroundColor:
                  filterReadOnly
                    ? alpha(theme.palette.primary.main, 0.16)
                    : 'transparent',
                border: `1px solid ${getControlBorderColor(theme)}`,
                color: filterReadOnly ? theme.palette.primary.light : theme.palette.text.secondary,
              }}
            />
          </ActionHelpTooltip>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, display: isShortViewport ? 'none' : 'block' }}
          >
            Click a row to toggle or add it.
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: { xs: 0.65, md: 0.75 },
          py: 0.65,
        }}
      >
        {managedItems.length === 0 ? (
          <Box
            sx={{
              p: 2.5,
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {searchTerm ? 'No columns match your search' : 'No columns available'}
            </Typography>
          </Box>
        ) : (
          <List
            dense
            disablePadding
            aria-label="Quick Fill columns"
            sx={{
              display: 'block',
            }}
          >
            {managedItems.map((item) => {
              const isExtra = item.source === 'extra';
              const { parameterName, alias, accessibleLabel } = getManagedColumnPresentation(item);
              const category = 'category' in item ? item.category : '';
              const validationHighlighted = normalizedHighlights.includes(
                item.label.trim().toUpperCase()
              );
              return (
                <ListItem
                  key={item.id}
                  disablePadding
                  ref={(node) => {
                    itemRefs.current[item.id] = node;
                  }}
                >
                  <ListItemButton
                    aria-label={accessibleLabel}
                    aria-pressed={item.source === 'available' ? undefined : item.checked}
                    onClick={() => handleItemClick(item)}
                    selected={item.checked}
                    data-validation-focus={validationHighlighted ? 'true' : undefined}
                    sx={{
                      py: isShortViewport ? 0.55 : 0.8,
                      px: 0.85,
                      gap: 1,
                      alignItems: 'flex-start',
                      minHeight: 30,
                      borderRadius: `${borderRadius.sm}px`,
                      border: `1px solid ${validationHighlighted || item.checked ? alpha(theme.palette.primary.main, 0.18) : 'transparent'}`,
                      mb: 0.25,
                      backgroundColor:
                        validationHighlighted
                          ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.06 : 0.1)
                          : item.checked
                            ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.045 : 0.075)
                          : 'transparent',
                      boxShadow: item.checked ? `inset 2px 0 0 ${alpha(theme.palette.primary.main, 0.8)}` : 'none',
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.045 : 0.075),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.1),
                        },
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.045 : 0.09),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        mt: 0.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: `${borderRadius.sm}px`,
                        border: `1px solid ${alpha(item.checked ? theme.palette.primary.main : theme.palette.divider, 0.26)}`,
                        backgroundColor: 'transparent',
                        color: item.checked ? theme.palette.primary.light : theme.palette.text.disabled,
                        flexShrink: 0,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: '0.8rem', opacity: item.checked ? 1 : 0.1 }} />
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.73rem', fontWeight: item.checked ? 600 : 500, flex: 1 }}
                            noWrap
                          >
                            {parameterName}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mt: 0.2,
                            lineHeight: 1.4,
                          }}
                        >
                          {[
                            alias || (item.source === 'available' ? category : 'Configured column'),
                            category && alias ? category : null,
                            isExtra ? 'Extra' : null,
                            item.isReadOnly ? 'RO' : null,
                            item.dataType || null,
                          ].filter(Boolean).join('  ·  ')}
                        </Typography>
                      }
                    />
                    {isExtra ? (
                      <ActionHelpTooltip help={quickFillActionTooltips.removeExtraColumn}>
                        <IconButton
                          size="small"
                          aria-label={`Remove extra column ${item.label}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveExtra(item.id);
                          }}
                          sx={{
                            p: 0.25,
                            color: theme.palette.text.disabled,
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                      </ActionHelpTooltip>
                    ) : null}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
});

PropertySelectionPanel.displayName = 'PropertySelectionPanel';

export default PropertySelectionPanel;
